import { useSyncExternalStore } from 'react';

import type { PushNotificationMetaData } from 'pkg/actions/notifications';

import { delay } from 'pkg/timings';

import type { MaterialSymbolVariant } from 'components/material-symbols/symbols';

export const FLASH_DECAY_TIME = 6000;

export interface FlashMessage {
	title: string;
	message?: string;
	additionalData?: PushNotificationMetaData;
}

interface FlashState {
	visible: boolean;
	message: FlashMessage | null;
	icon: MaterialSymbolVariant | null;
	status: number;
}

const defaultState: FlashState = {
	visible: false,
	message: null,
	icon: null,
	status: 0,
};

let state: FlashState = { ...defaultState };
let showVersion = 0;
const subscribers = new Set<() => void>();

function notify(): void {
	subscribers.forEach((fn) => fn());
}

function subscribe(fn: () => void): () => void {
	subscribers.add(fn);
	return () => {
		subscribers.delete(fn);
	};
}

function getSnapshot(): FlashState {
	return state;
}

export function setVisibility(visible: boolean): void {
	if (visible === state.visible) return;

	state = { ...state, visible };
	notify();
}

export async function show(
	message: FlashMessage,
	status?: number,
	icon?: MaterialSymbolVariant
): Promise<void> {
	const version = ++showVersion;

	state = {
		message,
		status: status ?? 0,
		icon: icon ?? 'info',
		visible: true,
	};
	notify();

	await delay(FLASH_DECAY_TIME);

	if (showVersion !== version) return;

	state = { ...state, visible: false };
	notify();
}

export function useFlash(): FlashState {
	return useSyncExternalStore(subscribe, getSnapshot);
}
